import { AnimatePresence, motion } from 'motion/react';

import { cn } from '@april9au/react-common/utils';

import { pressSpring } from '@/components/motion/tokens';
import { pressProps, stagedChild, tickVariants } from '@/components/motion/variants';
import { Icon } from '@/components/ui/icon';

/**
 * One tappable row in the language and audience lists. Selection is a filled
 * yellow tick with a navy glyph, plus a yellow border and a tinted ground —
 * never colour alone — and the whole row is the target, comfortably over 44 px.
 * Yellow marks the choice, blue stays the focus and wayfinding colour, so the
 * two never compete on the same tile.
 *
 * `indicator` says what the trailing glyph promises: a `check` for a choice
 * that stays on the screen, an arrow for one that advances immediately.
 *
 * The tile takes its place in the card's staged entrance from where it sits in
 * the list — `stagedChild` inherits the stage's label, so a list only has to
 * render in order and nothing counts indices. Colour and border stay CSS
 * transitions; movement and the tick are `motion`.
 */
export function ChoiceTile({
  selected,
  onClick,
  leading,
  label,
  sublabel,
  lang,
  indicator = 'check',
}: {
  selected: boolean;
  onClick: () => void;
  /** Flag, glyph or number shown before the label. */
  leading?: React.ReactNode;
  label: string;
  sublabel?: string;
  lang?: string;
  indicator?: 'check' | 'go';
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      {...stagedChild}
      {...pressProps}
      whileHover={{ y: -1 }}
      transition={pressSpring}
      className={cn(
        'bp-tappable group flex min-h-14 w-full items-center gap-4 rounded-2xl border-2 bg-white px-4 py-3 text-start',
        'transition-[background-color,border-color,box-shadow] duration-[var(--bp-duration-fast)] motion-reduce:transition-none',
        'focus-visible:ring-surf-blue/40 focus-visible:border-surf-blue focus-visible:ring-4 focus-visible:outline-none',
        selected
          ? 'border-bp-yellow bg-bp-yellow/[0.14] shadow-[0_6px_18px_-10px_rgb(2_20_66_/_0.5)]'
          : 'border-surf-gray hover:border-surf-blue/40 hover:bg-surf-blue/[0.02]',
      )}
    >
      {leading ? <span className="flex shrink-0 items-center">{leading}</span> : null}

      <span className="min-w-0 flex-1">
        <span className="text-bp-ink block text-lg leading-snug font-bold" lang={lang}>
          {label}
        </span>
        {sublabel ? <span className="text-bp-ink-muted block text-sm">{sublabel}</span> : null}
      </span>

      {indicator === 'go' ? (
        <Icon
          name="passport/arrow-right"
          aria-hidden
          className="text-surf-blue size-5 shrink-0 transition-transform duration-[var(--bp-duration-fast)] group-hover:translate-x-0.5 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0 rtl:rotate-180"
        />
      ) : (
        <span
          aria-hidden
          className={cn(
            'grid size-6 shrink-0 place-items-center rounded-full border-2 transition-colors duration-[var(--bp-duration-fast)] motion-reduce:transition-none',
            selected ? 'border-bp-yellow bg-bp-yellow text-bp-on-yellow' : 'border-surf-gray text-transparent',
          )}
        >
          {/* Reserved box; only the glyph pops, on the tap that set it. */}
          <AnimatePresence initial={false}>
            {selected ? (
              <motion.span key="tick" variants={tickVariants} initial="hidden" animate="shown" exit="hidden">
                <Icon name="passport/check" className="size-4" />
              </motion.span>
            ) : null}
          </AnimatePresence>
        </span>
      )}
    </motion.button>
  );
}
